import React, { useEffect, useState } from 'react';
import { SettingsIcon, BuildingIcon, UploadIcon, Trash2Icon, ChevronDownIcon, SaveIcon, CheckCircleIcon } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
type TabType = 'dealer' | 'design' | 'quote';
type DealerSubTabType = 'legal' | 'address' | 'contact';
export function SystemSettingsPage() {
  const {
    role,
    companyName,
    companyLogo,
    setCompanyLogo,
    officialCountry,
    setOfficialCountry
  } = useUser();
  const [activeTab, setActiveTab] = useState<TabType>('dealer');
  const [dealerSubTab, setDealerSubTab] = useState<DealerSubTabType>('legal');
  const [logoPreview, setLogoPreview] = useState<string | null>(companyLogo);
  // Legal data
  const [legalName, setLegalName] = useState('DAIRY GLOBAL EXPERTS LLC LN');
  const [taxId, setTaxId] = useState('INN 392949923233');
  const [billingCountry, setBillingCountry] = useState('');
  // Billing address
  const [lineAddress1, setLineAddress1] = useState('50 Centralna st');
  const [lineAddress2, setLineAddress2] = useState('');
  const [city, setCity] = useState('Hromy village, Uman district');
  const [stateProvince, setStateProvince] = useState('Cherkasy Region');
  const [zipCode, setZipCode] = useState('20343');
  const [distributionCountry, setDistributionCountry] = useState('Ukraine');
  // Contact
  const [phone, setPhone] = useState('+380 97 410 2423');
  const [email, setEmail] = useState('irrigation.dge@gmail.com');
  const [url, setUrl] = useState('www.dge.com.ua');
  const [dealerMarkup, setDealerMarkup] = useState('25.00');
  // Design Preferences - Irrigation Capacity
  const [applicationRate, setApplicationRate] = useState('1.30');
  const [applicationRateUnit, setApplicationRateUnit] = useState('l/s/ha');
  const [irrigationTimingMode, setIrrigationTimingMode] = useState<'hours' | 'percentage'>('hours');
  const [hoursPerDay, setHoursPerDay] = useState('18');
  const [daysPerWeek, setDaysPerWeek] = useState('7');
  const [percentageRate, setPercentageRate] = useState('75.00');
  // Design Preferences - General Configuration
  const [brand, setBrand] = useState('Pierce');
  const [model, setModel] = useState('CP600');
  const [electricalFrequency, setElectricalFrequency] = useState('60 Hz');
  const [profile, setProfile] = useState('Standard');
  // Design Preferences - Controls
  const [communicationType, setCommunicationType] = useState('GSM 4G');
  const [temperatureSensor, setTemperatureSensor] = useState(true);
  const [dosingPumpControl, setDosingPumpControl] = useState(false);
  const [stopInSlot, setStopInSlot] = useState(false);
  const [lightingArrestor, setLightingArrestor] = useState(false);
  // Design Preferences - Pivot Point
  const [pivotProfile, setPivotProfile] = useState('Standard');
  const [pivotProtectiveCoating, setPivotProtectiveCoating] = useState('Galvanized');
  const [standPipeDiameter, setStandPipeDiameter] = useState('8"5/8');
  const [heavyDuty, setHeavyDuty] = useState(false);
  // Design Preferences - Spans
  const [outletSpacing, setOutletSpacing] = useState('1.5 m');
  const [spansProtectiveCoating, setSpansProtectiveCoating] = useState('Galvanized');
  const [spansProfile, setSpansProfile] = useState('Standard');
  // Design Preferences - Drive Train
  const [motorCover, setMotorCover] = useState(true);
  const [centerDrive, setCenterDrive] = useState('UMC 35/2...');
  const [wdGearbox, setWdGearbox] = useState('Std. 52/1');
  const [tires, setTires] = useState('14.9x24');
  // Design Preferences - Electric Cables
  const [cableProtection, setCableProtection] = useState('Unshield');
  const [wireSize, setWireSize] = useState('4 mm²');
  // Design Preferences - Sprinklers
  const [maxSprinklerSpacing, setMaxSprinklerSpacing] = useState('1.50');
  const [sprinklerName, setSprinklerName] = useState('Nelson | Rotator-R3...');
  const [pad, setPad] = useState('Orange');
  const [drops, setDrops] = useState('Flex Drop with plast...');
  const [clearanceHeight, setClearanceHeight] = useState('2.60');
  const [regulator, setRegulator] = useState(true);
  const [regulatorBrand, setRegulatorBrand] = useState('Komet');
  const [nominalPressure, setNominalPressure] = useState('10 psi');
  const [verticalPosition, setVerticalPosition] = useState('Top');
  // Design Preferences - End Gun 1
  const [endGun1Brand, setEndGun1Brand] = useState('Komet');
  const [endGun1Model, setEndGun1Model] = useState('KPW HEAD-UP');
  const [endGun1Control, setEndGun1Control] = useState('Endgun Control wit...');
  // Design Preferences - End Gun 2
  const [endGun2Brand, setEndGun2Brand] = useState('');
  const [endGun2Model, setEndGun2Model] = useState('');
  const [endGun2Control, setEndGun2Control] = useState('');
  // Design Preferences - Booster Pump
  const [boosterPumpPower, setBoosterPumpPower] = useState('');
  // Save status tracking
  const [savingTab, setSavingTab] = useState<string | null>(null);
  const [savedTab, setSavedTab] = useState<string | null>(null);
  // Initialize state
  useEffect(() => {
    setLogoPreview(companyLogo);
  }, [companyLogo]);
  // Determine if user can edit logo and official country
  const canEditLogo = ['sales-manager', 'operations-manager', 'manager-business-unit', 'ceo', 'logistic', 'marketing', 'financial-manager', 'system-admin', 'dealer-admin', 'region-admin'].includes(role);
  const canEditOfficialCountry = canEditLogo;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveLogo = () => {
    setLogoPreview(null);
  };
  const handleSaveDealerInfo = () => {
    setSavingTab('dealer');
    setCompanyLogo(logoPreview);
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('dealer');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveDesignPreferences = () => {
    setSavingTab('design');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('design');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveQuote = () => {
    setSavingTab('quote');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('quote');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSave = () => {
    setCompanyLogo(logoPreview);
    // In a real app, we would save all the other fields here too
    alert('Settings saved successfully!');
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia', 'Ucrania', 'Ukraine', 'Reino Unido', 'Italia', 'Canadá'];
  const tabs = [{
    id: 'dealer' as TabType,
    label: 'Dealer Information'
  }, {
    id: 'design' as TabType,
    label: 'Design Preferences'
  }, {
    id: 'quote' as TabType,
    label: 'Quote'
  }];
  const dealerSubTabs = [{
    id: 'legal' as DealerSubTabType,
    label: 'Legal Data'
  }, {
    id: 'address' as DealerSubTabType,
    label: 'Billing Address'
  }, {
    id: 'contact' as DealerSubTabType,
    label: 'Contact'
  }];
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-10314">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-10315">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-10316"></div>
        <div className="absolute top-[40%] right-[10%] w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-10317"></div>
        <div className="absolute bottom-[10%] left-[25%] w-80 h-80 bg-teal-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20" data-id="element-10318"></div>
      </div>

      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 py-4 relative z-10" data-id="element-10319">
        <div className="flex justify-between items-center" data-id="element-10320">
          <div className="flex items-center" data-id="element-10321">
            <h1 className="text-xl font-bold text-white flex items-center" data-id="element-10322">
              <SettingsIcon size={24} className="mr-2 text-blue-400" data-id="element-10323" />
              System Settings
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 relative z-10" data-id="element-10324">
        <div className="flex" data-id="element-10325">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-10326">
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-10327" />}
            </button>)}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6 relative z-10" data-id="element-10328">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-10329">
          {/* Dealer Information Tab */}
          {activeTab === 'dealer' && <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 overflow-hidden" data-id="element-10330">
              {/* Company Header Section */}
              <div className="px-6 py-4 border-b border-white/10" data-id="element-10331">
                <div className="flex items-center justify-between gap-4" data-id="element-10332">
                  {/* Left side: Logo and Company Name */}
                  <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-10333">
                    {/* Logo Section */}
                    <div className="relative flex-shrink-0" data-id="element-10334">
                      {logoPreview ? <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 border border-white/10" data-id="element-10335">
                          <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" data-id="element-10336" />
                        </div> : <div className="w-14 h-14 rounded-lg bg-blue-600/20 flex items-center justify-center border border-white/10" data-id="element-10337">
                          <BuildingIcon size={28} className="text-blue-400" data-id="element-10338" />
                        </div>}

                      {/* Logo Upload/Remove Controls */}
                      {canEditLogo && <div className="absolute -bottom-1 -right-1 flex gap-1" data-id="element-10339">
                          <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-10340">
                            <UploadIcon size={10} data-id="element-10341" />
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-10342" />
                          </label>
                          {logoPreview && <button onClick={handleRemoveLogo} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-10343">
                              <Trash2Icon size={10} data-id="element-10344" />
                            </button>}
                        </div>}
                    </div>

                    {/* Company Name */}
                    <div className="flex-1 min-w-0" data-id="element-10345">
                      <h3 className="text-lg font-semibold text-white truncate" data-id="element-10346">
                        {companyName}
                      </h3>
                    </div>
                  </div>

                  {/* Right side: Distribution Country */}
                  <div className="flex-shrink-0" data-id="element-10347">
                    <p className="text-xs text-blue-300 mb-1" data-id="element-10348">
                      Distribuidor oficial en
                    </p>
                    {canEditOfficialCountry ? <div className="relative" data-id="element-10349">
                        <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} className="appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10350">
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-10351">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-blue-300" data-id="element-10352">
                          <ChevronDownIcon size={14} data-id="element-10353" />
                        </div>
                      </div> : <p className="text-sm font-medium text-white" data-id="element-10354">
                        {distributionCountry || 'No country set'}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Sub Tabs */}
              <div className="flex border-b border-white/10 px-6" data-id="element-10355">
                {dealerSubTabs.map(tab => <button key={tab.id} onClick={() => setDealerSubTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${dealerSubTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-10356">
                    {tab.label}
                    {dealerSubTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-10357" />}
                  </button>)}
              </div>

              {/* Sub Tab Content */}
              <div className="p-6" data-id="element-10358">
                {/* Legal Data Sub Tab */}
                {dealerSubTab === 'legal' && <div className="space-y-5" data-id="element-10359">
                    <div data-id="element-10360">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10361">
                        Legal name <span className="text-red-400" data-id="element-10362">*</span>
                      </label>
                      <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Company legal name" data-id="element-10363" />
                    </div>

                    <div data-id="element-10364">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10365">
                        TAX ID / VAT ID / NIF{' '}
                        <span className="text-red-400" data-id="element-10366">*</span>
                      </label>
                      <input type="text" value={taxId} onChange={e => setTaxId(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Tax identification number" data-id="element-10367" />
                    </div>
                  </div>}

                {/* Address Sub Tab */}
                {dealerSubTab === 'address' && <div className="space-y-5" data-id="element-10368">
                    <div data-id="element-10369">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10370">
                        Address line 1 <span className="text-red-400" data-id="element-10371">*</span>
                      </label>
                      <input type="text" value={lineAddress1} onChange={e => setLineAddress1(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Street, number" data-id="element-10372" />
                    </div>

                    <div data-id="element-10373">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10374">
                        Address line 2
                      </label>
                      <input type="text" value={lineAddress2} onChange={e => setLineAddress2(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Apartment, suite, etc. (optional)" data-id="element-10375" />
                    </div>

                    <div data-id="element-10376">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10377">
                        City <span className="text-red-400" data-id="element-10378">*</span>
                      </label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="City" data-id="element-10379" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-10380">
                      <div data-id="element-10381">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10382">
                          State / Province
                        </label>
                        <input type="text" value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="State or province" data-id="element-10383" />
                      </div>

                      <div data-id="element-10384">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10385">
                          Zip code
                        </label>
                        <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Zip code" data-id="element-10386" />
                      </div>
                    </div>

                    <div data-id="element-10387">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10388">
                        Billing country <span className="text-red-400" data-id="element-10389">*</span>
                      </label>
                      <div className="relative" data-id="element-10390">
                        <select value={billingCountry} onChange={e => setBillingCountry(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10391">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10392">
                            Please, select country
                          </option>
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-10393">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10394">
                          <ChevronDownIcon size={16} data-id="element-10395" />
                        </div>
                      </div>
                      {!billingCountry && <p className="text-red-400 text-xs mt-1" data-id="element-10396">
                          Please, select country
                        </p>}
                    </div>
                  </div>}

                {/* Contact Sub Tab */}
                {dealerSubTab === 'contact' && <div className="space-y-5" data-id="element-10397">
                    <div data-id="element-10398">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10399">
                        Phone
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="+1 234 567 8900" data-id="element-10400" />
                    </div>

                    <div data-id="element-10401">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10402">
                        Email
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="company@email.com" data-id="element-10403" />
                    </div>

                    <div data-id="element-10404">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10405">
                        Website
                      </label>
                      <input type="url" value={url} onChange={e => setUrl(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="www.company.com" data-id="element-10406" />
                    </div>
                  </div>}
              </div>

              {/* Save Button for Dealer Information (common for all 3 sub-tabs) */}
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end bg-black/10 rounded-b-xl" data-id="element-10407">
                <button onClick={handleSaveDealerInfo} disabled={savingTab === 'dealer'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-10408">
                  {savingTab === 'dealer' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10409" />
                      Saving...
                    </> : savedTab === 'dealer' ? <>
                      <CheckCircleIcon size={16} data-id="element-10410" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-10411" />
                      Save Dealer Information
                    </>}
                </button>
              </div>
            </div>}

          {/* Design Preferences Tab */}
          {activeTab === 'design' && <div className="space-y-6" data-id="element-10412">
              {/* Irrigation Capacity Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10413">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10414">
                  Irrigation Capacity
                </h3>
                <div className="space-y-5" data-id="element-10415">
                  <div data-id="element-10416">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10417">
                      Application rate
                    </label>
                    <div className="flex gap-3" data-id="element-10418">
                      <input type="number" step="0.01" value={applicationRate} onChange={e => setApplicationRate(e.target.value)} className="w-32 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10419" />
                      <div className="relative flex-1" data-id="element-10420">
                        <select value={applicationRateUnit} onChange={e => setApplicationRateUnit(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10421">
                          <option value="l/s/ha" className="bg-slate-800 text-white" data-id="element-10422">
                            l/s/ha
                          </option>
                          <option value="mm/h" className="bg-slate-800 text-white" data-id="element-10423">
                            mm/h
                          </option>
                          <option value="gpm/ac" className="bg-slate-800 text-white" data-id="element-10424">
                            gpm/ac
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10425">
                          <ChevronDownIcon size={16} data-id="element-10426" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10427">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10428">
                      Irrigation timing rate
                    </label>
                    <div className="space-y-3" data-id="element-10429">
                      <div className="flex items-center gap-3" data-id="element-10430">
                        <input type="radio" id="timing-hours" checked={irrigationTimingMode === 'hours'} onChange={() => setIrrigationTimingMode('hours')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-10431" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-10432">
                          <input type="number" value={hoursPerDay} onChange={e => setHoursPerDay(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10433" />
                          <span className="text-white text-sm" data-id="element-10434">h/day</span>
                          <input type="number" value={daysPerWeek} onChange={e => setDaysPerWeek(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10435" />
                          <span className="text-white text-sm" data-id="element-10436">days/week</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3" data-id="element-10437">
                        <input type="radio" id="timing-percentage" checked={irrigationTimingMode === 'percentage'} onChange={() => setIrrigationTimingMode('percentage')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-10438" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-10439">
                          <input type="number" step="0.01" value={percentageRate} onChange={e => setPercentageRate(e.target.value)} disabled={irrigationTimingMode !== 'percentage'} className="w-24 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10440" />
                          <span className="text-white text-sm" data-id="element-10441">% 18h/24h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* General Configuration Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10442">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10443">
                  General Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-10444">
                  <div data-id="element-10445">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10446">
                      Brand
                    </label>
                    <div className="relative" data-id="element-10447">
                      <select value={brand} onChange={e => setBrand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10448">
                        <option value="Pierce" className="bg-slate-800 text-white" data-id="element-10449">
                          Pierce
                        </option>
                        <option value="Reinke" className="bg-slate-800 text-white" data-id="element-10450">
                          Reinke
                        </option>
                        <option value="Valley" className="bg-slate-800 text-white" data-id="element-10451">
                          Valley
                        </option>
                        <option value="Zimmatic" className="bg-slate-800 text-white" data-id="element-10452">
                          Zimmatic
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10453">
                        <ChevronDownIcon size={16} data-id="element-10454" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10455">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10456">
                      Model
                    </label>
                    <div className="relative" data-id="element-10457">
                      <select value={model} onChange={e => setModel(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10458">
                        <option value="CP600" className="bg-slate-800 text-white" data-id="element-10459">
                          CP600
                        </option>
                        <option value="CP900" className="bg-slate-800 text-white" data-id="element-10460">
                          CP900
                        </option>
                        <option value="CP1200" className="bg-slate-800 text-white" data-id="element-10461">
                          CP1200
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10462">
                        <ChevronDownIcon size={16} data-id="element-10463" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10464">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10465">
                      Electrical Frequency
                    </label>
                    <div className="relative" data-id="element-10466">
                      <select value={electricalFrequency} onChange={e => setElectricalFrequency(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10467">
                        <option value="60 Hz" className="bg-slate-800 text-white" data-id="element-10468">
                          60 Hz
                        </option>
                        <option value="50 Hz" className="bg-slate-800 text-white" data-id="element-10469">
                          50 Hz
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10470">
                        <ChevronDownIcon size={16} data-id="element-10471" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10472">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10473">
                      Profile
                    </label>
                    <div className="relative" data-id="element-10474">
                      <select value={profile} onChange={e => setProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10475">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-10476">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-10477">
                          Heavy
                        </option>
                        <option value="Extra Heavy" className="bg-slate-800 text-white" data-id="element-10478">
                          Extra Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10479">
                        <ChevronDownIcon size={16} data-id="element-10480" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10481">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10482">
                  Controls
                </h3>
                <div className="space-y-5" data-id="element-10483">
                  <div data-id="element-10484">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10485">
                      Communication type
                    </label>
                    <div className="relative" data-id="element-10486">
                      <select value={communicationType} onChange={e => setCommunicationType(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10487">
                        <option value="GSM 4G" className="bg-slate-800 text-white" data-id="element-10488">
                          GSM 4G
                        </option>
                        <option value="GSM 3G" className="bg-slate-800 text-white" data-id="element-10489">
                          GSM 3G
                        </option>
                        <option value="Radio" className="bg-slate-800 text-white" data-id="element-10490">
                          Radio
                        </option>
                        <option value="WiFi" className="bg-slate-800 text-white" data-id="element-10491">
                          WiFi
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10492">
                        <ChevronDownIcon size={16} data-id="element-10493" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10494">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10495">
                      <input type="checkbox" checked={temperatureSensor} onChange={e => setTemperatureSensor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10496" />
                      <span className="text-white text-sm" data-id="element-10497">
                        Temperature Sensor
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10498">
                      <input type="checkbox" checked={dosingPumpControl} onChange={e => setDosingPumpControl(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10499" />
                      <span className="text-white text-sm" data-id="element-10500">
                        Dosing Pump Control
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10501">
                      <input type="checkbox" checked={stopInSlot} onChange={e => setStopInSlot(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10502" />
                      <span className="text-white text-sm" data-id="element-10503">
                        Stop in slot on Pivot Point
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10504">
                      <input type="checkbox" checked={lightingArrestor} onChange={e => setLightingArrestor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10505" />
                      <span className="text-white text-sm" data-id="element-10506">
                        Lighting arrestor
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Pivot Point Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10507">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10508">
                  Pivot Point
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-10509">
                  <div data-id="element-10510">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10511">
                      Profile
                    </label>
                    <div className="relative" data-id="element-10512">
                      <select value={pivotProfile} onChange={e => setPivotProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10513">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-10514">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-10515">
                          Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10516">
                        <ChevronDownIcon size={16} data-id="element-10517" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10518">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10519">
                      Protective Coating
                    </label>
                    <div className="relative" data-id="element-10520">
                      <select value={pivotProtectiveCoating} onChange={e => setPivotProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10521">
                        <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-10522">
                          Galvanized
                        </option>
                        <option value="Painted" className="bg-slate-800 text-white" data-id="element-10523">
                          Painted
                        </option>
                        <option value="Stainless" className="bg-slate-800 text-white" data-id="element-10524">
                          Stainless
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10525">
                        <ChevronDownIcon size={16} data-id="element-10526" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10527">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10528">
                      Stand Pipe Diameter
                    </label>
                    <div className="relative" data-id="element-10529">
                      <select value={standPipeDiameter} onChange={e => setStandPipeDiameter(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10530">
                        <option value='8"5/8' className="bg-slate-800 text-white" data-id="element-10531">
                          8"5/8
                        </option>
                        <option value='6"5/8' className="bg-slate-800 text-white" data-id="element-10532">
                          6"5/8
                        </option>
                        <option value="10" className="bg-slate-800 text-white" data-id="element-10533">
                          10
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10534">
                        <ChevronDownIcon size={16} data-id="element-10535" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5" data-id="element-10536">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-10537">
                    <input type="checkbox" checked={heavyDuty} onChange={e => setHeavyDuty(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10538" />
                    <span className="text-white text-sm" data-id="element-10539">Heavy duty</span>
                  </label>
                </div>
              </div>

              {/* Spans and Drive Train - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-10540">
                {/* Spans Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10541">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10542">
                    Spans
                  </h3>
                  <div className="space-y-5" data-id="element-10543">
                    <div data-id="element-10544">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10545">
                        Outlet Spacing
                      </label>
                      <div className="relative" data-id="element-10546">
                        <select value={outletSpacing} onChange={e => setOutletSpacing(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10547">
                          <option value="1.5 m" className="bg-slate-800 text-white" data-id="element-10548">
                            1.5 m
                          </option>
                          <option value="3 m" className="bg-slate-800 text-white" data-id="element-10549">
                            3 m
                          </option>
                          <option value="1.14 m" className="bg-slate-800 text-white" data-id="element-10550">
                            1.14 m
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10551">
                          <ChevronDownIcon size={16} data-id="element-10552" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10553">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10554">
                        Protective Coating
                      </label>
                      <div className="relative" data-id="element-10555">
                        <select value={spansProtectiveCoating} onChange={e => setSpansProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10556">
                          <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-10557">
                            Galvanized
                          </option>
                          <option value="Painted" className="bg-slate-800 text-white" data-id="element-10558">
                            Painted
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10559">
                          <ChevronDownIcon size={16} data-id="element-10560" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10561">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10562">
                        Profile
                      </label>
                      <div className="relative" data-id="element-10563">
                        <select value={spansProfile} onChange={e => setSpansProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10564">
                          <option value="Standard" className="bg-slate-800 text-white" data-id="element-10565">
                            Standard
                          </option>
                          <option value="Heavy" className="bg-slate-800 text-white" data-id="element-10566">
                            Heavy
                          </option>
                          <option value="CN+" className="bg-slate-800 text-white" data-id="element-10567">
                            CN+
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10568">
                          <ChevronDownIcon size={16} data-id="element-10569" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Drive Train Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10570">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10571">
                    Drive Train
                  </h3>
                  <div className="space-y-5" data-id="element-10572">
                    <div data-id="element-10573">
                      <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-10574">
                        <input type="checkbox" checked={motorCover} onChange={e => setMotorCover(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10575" />
                        <span className="text-white text-sm" data-id="element-10576">Motor cover</span>
                      </label>
                    </div>

                    <div data-id="element-10577">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10578">
                        Center Drive
                      </label>
                      <div className="relative" data-id="element-10579">
                        <select value={centerDrive} onChange={e => setCenterDrive(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10580">
                          <option value="UMC 35/2..." className="bg-slate-800 text-white" data-id="element-10581">
                            UMC 35/2...
                          </option>
                          <option value="UMC 40/2" className="bg-slate-800 text-white" data-id="element-10582">
                            UMC 40/2
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10583">
                          <ChevronDownIcon size={16} data-id="element-10584" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10585">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10586">
                        WD Gearbox
                      </label>
                      <div className="relative" data-id="element-10587">
                        <select value={wdGearbox} onChange={e => setWdGearbox(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10588">
                          <option value="Std. 52/1" className="bg-slate-800 text-white" data-id="element-10589">
                            Std. 52/1
                          </option>
                          <option value="Std. 40/1" className="bg-slate-800 text-white" data-id="element-10590">
                            Std. 40/1
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10591">
                          <ChevronDownIcon size={16} data-id="element-10592" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10593">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10594">
                        Tires
                      </label>
                      <div className="relative" data-id="element-10595">
                        <select value={tires} onChange={e => setTires(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10596">
                          <option value="14.9x24" className="bg-slate-800 text-white" data-id="element-10597">
                            14.9x24
                          </option>
                          <option value="11.2x24" className="bg-slate-800 text-white" data-id="element-10598">
                            11.2x24
                          </option>
                          <option value="11.2x38" className="bg-slate-800 text-white" data-id="element-10599">
                            11.2x38
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10600">
                          <ChevronDownIcon size={16} data-id="element-10601" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Electric Cables Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10602">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10603">
                  Electric Cables
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-10604">
                  <div data-id="element-10605">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10606">
                      Electric cable protection
                    </label>
                    <div className="relative" data-id="element-10607">
                      <select value={cableProtection} onChange={e => setCableProtection(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10608">
                        <option value="Unshield" className="bg-slate-800 text-white" data-id="element-10609">
                          Unshield
                        </option>
                        <option value="Shield" className="bg-slate-800 text-white" data-id="element-10610">
                          Shield
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10611">
                        <ChevronDownIcon size={16} data-id="element-10612" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-10613">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10614">
                      Wire size
                    </label>
                    <div className="relative" data-id="element-10615">
                      <select value={wireSize} onChange={e => setWireSize(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10616">
                        <option value="4 mm²" className="bg-slate-800 text-white" data-id="element-10617">
                          4 mm²
                        </option>
                        <option value="6 mm²" className="bg-slate-800 text-white" data-id="element-10618">
                          6 mm²
                        </option>
                        <option value="10 mm²" className="bg-slate-800 text-white" data-id="element-10619">
                          10 mm²
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10620">
                        <ChevronDownIcon size={16} data-id="element-10621" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sprinklers Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10622">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10623">
                  Sprinklers
                </h3>

                {/* Sprinkler Preferences Subsection */}
                <div className="mb-6" data-id="element-10624">
                  <h4 className="text-sm font-medium text-blue-200 mb-4" data-id="element-10625">
                    Sprinkler Preferences
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-10626">
                    <div data-id="element-10627">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10628">
                        Max Sprinkler Spacing
                      </label>
                      <div className="flex gap-2" data-id="element-10629">
                        <input type="number" step="0.01" value={maxSprinklerSpacing} onChange={e => setMaxSprinklerSpacing(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="1.50" data-id="element-10630" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-10631">
                          m
                        </span>
                      </div>
                    </div>

                    <div data-id="element-10632">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10633">
                        Name
                      </label>
                      <div className="relative" data-id="element-10634">
                        <select value={sprinklerName} onChange={e => setSprinklerName(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10635">
                          <option value="Nelson | Rotator-R3..." className="bg-slate-800 text-white" data-id="element-10636">
                            Nelson | Rotator-R3...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10637">
                          <ChevronDownIcon size={16} data-id="element-10638" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10639">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10640">
                        Pad
                      </label>
                      <div className="relative" data-id="element-10641">
                        <select value={pad} onChange={e => setPad(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10642">
                          <option value="Orange" className="bg-slate-800 text-white" data-id="element-10643">
                            Orange
                          </option>
                          <option value="Blue" className="bg-slate-800 text-white" data-id="element-10644">
                            Blue
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10645">
                          <ChevronDownIcon size={16} data-id="element-10646" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10647">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10648">
                        Drops
                      </label>
                      <div className="relative" data-id="element-10649">
                        <select value={drops} onChange={e => setDrops(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10650">
                          <option value="Flex Drop with plast..." className="bg-slate-800 text-white" data-id="element-10651">
                            Flex Drop with plast...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10652">
                          <ChevronDownIcon size={16} data-id="element-10653" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10654">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10655">
                        Clearance height
                      </label>
                      <div className="flex gap-2" data-id="element-10656">
                        <input type="number" step="0.01" value={clearanceHeight} onChange={e => setClearanceHeight(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10657" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-10658">
                          m
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10 my-6" data-id="element-10659"></div>

                {/* Regulator Subsection */}
                <div data-id="element-10660">
                  <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-10661">
                    <input type="checkbox" checked={regulator} onChange={e => setRegulator(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-10662" />
                    <h4 className="text-sm font-medium text-blue-200" data-id="element-10663">
                      Regulator
                    </h4>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-10664">
                    <div data-id="element-10665">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10666">
                        Brand
                      </label>
                      <div className="relative" data-id="element-10667">
                        <select value={regulatorBrand} onChange={e => setRegulatorBrand(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10668">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-10669">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-10670">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10671">
                          <ChevronDownIcon size={16} data-id="element-10672" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10673">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10674">
                        Nominal pressure
                      </label>
                      <div className="relative" data-id="element-10675">
                        <select value={nominalPressure} onChange={e => setNominalPressure(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10676">
                          <option value="10 psi" className="bg-slate-800 text-white" data-id="element-10677">
                            10 psi
                          </option>
                          <option value="15 psi" className="bg-slate-800 text-white" data-id="element-10678">
                            15 psi
                          </option>
                          <option value="6 psi" className="bg-slate-800 text-white" data-id="element-10679">
                            6 psi
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10680">
                          <ChevronDownIcon size={16} data-id="element-10681" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10682">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10683">
                        Vertical position
                      </label>
                      <div className="relative" data-id="element-10684">
                        <select value={verticalPosition} onChange={e => setVerticalPosition(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10685">
                          <option value="Top" className="bg-slate-800 text-white" data-id="element-10686">
                            Top
                          </option>
                          <option value="Bottom" className="bg-slate-800 text-white" data-id="element-10687">
                            Bottom
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10688">
                          <ChevronDownIcon size={16} data-id="element-10689" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* End Guns and Booster Pump - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-10690">
                {/* End Gun 1 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10691">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10692">
                    End Gun 1
                  </h3>
                  <div className="space-y-5" data-id="element-10693">
                    <div data-id="element-10694">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10695">
                        Brand
                      </label>
                      <div className="relative" data-id="element-10696">
                        <select value={endGun1Brand} onChange={e => setEndGun1Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10697">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-10698">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-10699">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10700">
                          <ChevronDownIcon size={16} data-id="element-10701" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10702">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10703">
                        Model
                      </label>
                      <div className="relative" data-id="element-10704">
                        <select value={endGun1Model} onChange={e => setEndGun1Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10705">
                          <option value="KPW HEAD-UP" className="bg-slate-800 text-white" data-id="element-10706">
                            KPW HEAD-UP
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10707">
                          <ChevronDownIcon size={16} data-id="element-10708" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10709">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10710">
                        Control
                      </label>
                      <div className="relative" data-id="element-10711">
                        <select value={endGun1Control} onChange={e => setEndGun1Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10712">
                          <option value="Endgun Control wit..." className="bg-slate-800 text-white" data-id="element-10713">
                            Endgun Control wit...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10714">
                          <ChevronDownIcon size={16} data-id="element-10715" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* End Gun 2 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10716">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10717">
                    End Gun 2
                  </h3>
                  <div className="space-y-5" data-id="element-10718">
                    <div data-id="element-10719">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10720">
                        Brand
                      </label>
                      <div className="relative" data-id="element-10721">
                        <select value={endGun2Brand} onChange={e => setEndGun2Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10722">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10723">
                            Select brand
                          </option>
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-10724">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-10725">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10726">
                          <ChevronDownIcon size={16} data-id="element-10727" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10728">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10729">
                        Model
                      </label>
                      <div className="relative" data-id="element-10730">
                        <select value={endGun2Model} onChange={e => setEndGun2Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10731">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10732">
                            Select model
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10733">
                          <ChevronDownIcon size={16} data-id="element-10734" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-10735">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10736">
                        Control
                      </label>
                      <div className="relative" data-id="element-10737">
                        <select value={endGun2Control} onChange={e => setEndGun2Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10738">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10739">
                            Select control
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10740">
                          <ChevronDownIcon size={16} data-id="element-10741" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booster Pump */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10742">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-10743">
                    Booster Pump
                  </h3>
                  <div className="space-y-5" data-id="element-10744">
                    <div data-id="element-10745">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10746">
                        Power
                      </label>
                      <div className="relative" data-id="element-10747">
                        <select value={boosterPumpPower} onChange={e => setBoosterPumpPower(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-10748">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-10749">
                            Select power
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-10750">
                          <ChevronDownIcon size={16} data-id="element-10751" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Design Preferences */}
              <div className="flex items-center justify-end pt-2" data-id="element-10752">
                <button onClick={handleSaveDesignPreferences} disabled={savingTab === 'design'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-10753">
                  {savingTab === 'design' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10754" />
                      Saving...
                    </> : savedTab === 'design' ? <>
                      <CheckCircleIcon size={16} data-id="element-10755" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-10756" />
                      Save Design Preferences
                    </>}
                </button>
              </div>
            </div>}

          {/* Quote Tab */}
          {activeTab === 'quote' && <div className="space-y-6" data-id="element-10757">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-10758">
                <h3 className="text-white font-semibold text-base mb-2 pb-3 border-b border-white/10" data-id="element-10759">
                  Dealer Mark-up
                </h3>
                <p className="text-blue-300/70 text-sm mb-6" data-id="element-10760">
                  This is the default margin percentage applied to all quotes
                  generated for your customers. When a new quote is created,
                  this mark-up will be automatically added on top of the base
                  price to calculate the final customer price. You can also
                  configure a different mark-up for each individual customer,
                  and you can still adjust it on individual quotes if needed.
                </p>
                <div data-id="element-10761">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-10762">
                    Default mark-up <span className="text-red-400" data-id="element-10763">*</span>
                  </label>
                  <div className="relative w-48" data-id="element-10764">
                    <input type="number" step="0.01" value={dealerMarkup} onChange={e => setDealerMarkup(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="25.00" data-id="element-10765" />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300" data-id="element-10766">
                      %
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Quote */}
              <div className="flex items-center justify-end pt-2" data-id="element-10767">
                <button onClick={handleSaveQuote} disabled={savingTab === 'quote'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-10768">
                  {savingTab === 'quote' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10769" />
                      Saving...
                    </> : savedTab === 'quote' ? <>
                      <CheckCircleIcon size={16} data-id="element-10770" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-10771" />
                      Save Quote Settings
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}